import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API_URL = import.meta.env.VITE_API_URL || "https://eleven11-i6v4.onrender.com";

const REASONS = [
  "Wrong part received",
  "Part does not fit my watercraft",
  "Item arrived damaged",
  "Part is not as described",
  "Changed my mind",
  "Other",
];

const card = {
  borderRadius: 20,
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(24px)",
  border: "1px solid rgba(255,255,255,0.8)",
  boxShadow: "0 20px 50px rgba(24,35,60,0.09)",
  padding: "20px 18px",
  marginBottom: 14,
};

function SectionLabel({ text }) {
  return (
    <p style={{ margin: "0 0 16px", fontSize: 11, fontWeight: 900, color: "#70747e", letterSpacing: 2.2 }}>
      {text}
    </p>
  );
}

function FinePrintRow({ bold, text }) {
  return (
    <div style={{ padding: "14px 0", borderTop: "1px solid rgba(0,0,0,0.05)" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#111", marginBottom: 5 }}>{bold}</div>
      <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>{text}</div>
    </div>
  );
}

export default function SupportScreenMobile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("return");
  const [form, setForm] = useState({ orderNumber: "", reason: "", details: "", email: user?.email || "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.orderNumber || !form.reason || !form.email) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/returns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, userId: user?.id || "" })
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setError("Submission failed. Please try again.");
    } catch {
      setError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#f5f7fb 0%,#eef1f7 45%,#f7f8fb 100%)", fontFamily: "-apple-system, BlinkMacSystemFont, Inter, system-ui", paddingBottom: 50 }}>
      <div style={{ padding: "24px 16px 0" }}>

        {/* Header */}
        <button onClick={() => navigate("/account")} style={{ background: "none", border: "none", fontSize: 16, fontWeight: 700, color: "#0a84ff", cursor: "pointer", padding: 0, marginBottom: 14 }}>
          ‹ Back
        </button>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 2.4, color: "#7b7f89" }}>ELEVEN SUPPORT</p>
        <h1 style={{ margin: "4px 0 6px", fontSize: 30, letterSpacing: -1, fontWeight: 800, color: "#050505" }}>Returns & Support</h1>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
          We stand behind every part we sell. Here's how our return process works.
        </p>

        {/* Eligibility Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div style={{ borderRadius: 18, padding: "16px 14px", background: "#ecfdf5", border: "1px solid #6ee7b7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#111" }}>Eligible</span>
            </div>
            {["Used condition parts", "Within 7 days", "Original condition", "Non-electrical"].map((item, i) => (
              <div key={i} style={{ fontSize: 12, color: "#374151", marginBottom: 5, display: "flex", gap: 6 }}>
                <span style={{ color: "#6b7280" }}>•</span>{item}
              </div>
            ))}
          </div>
          <div style={{ borderRadius: 18, padding: "16px 14px", background: "#fef2f2", border: "1px solid #fca5a5" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#111" }}>Not Eligible</span>
            </div>
            {["Brand New parts", "Electrical parts", "After 7 days", "New damage"].map((item, i) => (
              <div key={i} style={{ fontSize: 12, color: "#374151", marginBottom: 5, display: "flex", gap: 6 }}>
                <span style={{ color: "#6b7280" }}>•</span>{item}
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div style={card}>
          <SectionLabel text="HOW RETURNS WORK" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { n: "01", title: "Submit a Request", desc: "Fill out the return form below with your order number and reason." },
              { n: "02", title: "We Review", desc: "Our team reviews your request within 1–2 business days." },
              { n: "03", title: "Shipping Label", desc: "If approved, we email you a prepaid return shipping label." },
              { n: "04", title: "Store Credit", desc: "Once received and inspected, store credit is issued." },
            ].map(step => (
              <div key={step.n} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#e5e7eb", letterSpacing: -1, minWidth: 36 }}>{step.n}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#111", marginBottom: 3 }}>{step.title}</div>
                  <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Policy — collapsible */}
        <div style={card}>
          <button onClick={() => setPolicyOpen(!policyOpen)} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: 0 }}>
            <SectionLabel text="RETURN POLICY — FINE PRINT" />
            <span style={{ fontSize: 18, color: "#9ca3af", display: "inline-block", transform: policyOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", marginTop: -14 }}>⌄</span>
          </button>
          {policyOpen && (
            <div>
              {[
                { bold: "Return Window", text: "Returns must be initiated within 7 days of delivery. Requests submitted after this window will not be accepted." },
                { bold: "Eligible Items", text: "Only Used condition parts are eligible. Brand New parts are final sale." },
                { bold: "Electrical Parts — No Exceptions", text: "Starters, CDI units, rectifiers, ignition coils, gauges, wiring harnesses, and sensors are strictly non-returnable." },
                { bold: "Restocking Fee", text: "A 20% restocking fee is applied to all approved returns, deducted from store credit issued." },
                { bold: "Return Shipping", text: "ELEVEN provides a prepaid shipping label. The cost is covered under the 20% restocking fee." },
                { bold: "Refund Method", text: "All approved returns are refunded as ELEVEN store credit only. No cash refunds or card reversals." },
                { bold: "Condition on Return", text: "Parts must be returned in the same condition as received." },
                { bold: "Abuse & Fraud", text: "ELEVEN reserves the right to refuse returns showing patterns of abuse." },
              ].map((row, i) => <FinePrintRow key={i} bold={row.bold} text={row.text} />)}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          {["return", "contact"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: "13px", borderRadius: 14,
              border: tab === t ? "none" : "1px solid #E0E3EA",
              background: tab === t ? "#111" : "rgba(255,255,255,0.7)",
              color: tab === t ? "#fff" : "#6b7280",
              fontSize: 14, fontWeight: 700, cursor: "pointer"
            }}>
              {t === "return" ? "Start a Return" : "Contact Support"}
            </button>
          ))}
        </div>

        {/* Return Form */}
        {tab === "return" && (
          <div style={card}>
            <SectionLabel text="RETURN REQUEST" />

            {submitted ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#ecfdf5", border: "2px solid #6ee7b7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#059669", margin: "0 auto 16px", fontWeight: 900 }}>✓</div>
                <h3 style={{ margin: "0 0 10px", fontSize: 20, fontWeight: 800 }}>Request Submitted</h3>
                <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, margin: "0 0 20px" }}>
                  We'll respond within 1–2 business days. If approved, a prepaid label will be sent to <strong>{form.email}</strong>.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ orderNumber: "", reason: "", details: "", email: user?.email || "" }); }}
                  style={{ background: "none", border: "1px solid #E0E3EA", borderRadius: 999, padding: "11px 22px", fontSize: 14, fontWeight: 700, color: "#374151", cursor: "pointer" }}
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <>
                <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "#92400e", marginBottom: 20, lineHeight: 1.6 }}>
                  ⚠ Returns are only accepted for <strong>Used condition</strong> parts. Electrical parts and Brand New items are not eligible.
                </div>

                {[
                  { label: "Order Number", field: "orderNumber", placeholder: "e.g. ORD-2024-00123" },
                  { label: "Email Address", field: "email", placeholder: "you@example.com" },
                ].map(f => (
                  <div key={f.field} style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 7 }}>{f.label}</label>
                    <input
                      style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #E0E3EA", background: "#fff", fontSize: 15, fontWeight: 500, color: "#111", outline: "none", boxSizing: "border-box" }}
                      placeholder={f.placeholder}
                      value={form[f.field]}
                      onChange={e => update(f.field, e.target.value)}
                    />
                  </div>
                ))}

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 7 }}>Reason for Return</label>
                  <select
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #E0E3EA", background: "#fff", fontSize: 15, color: form.reason ? "#111" : "#9ca3af", outline: "none", boxSizing: "border-box" }}
                    value={form.reason}
                    onChange={e => update("reason", e.target.value)}
                  >
                    <option value="" disabled>Select a reason...</option>
                    {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 7 }}>
                    Additional Details <span style={{ color: "#9ca3af", fontWeight: 500 }}>(optional)</span>
                  </label>
                  <textarea
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #E0E3EA", background: "#fff", fontSize: 15, color: "#111", outline: "none", boxSizing: "border-box", height: 100, resize: "vertical", fontFamily: "inherit" }}
                    placeholder="Describe the issue in more detail..."
                    value={form.details}
                    onChange={e => update("details", e.target.value)}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "#0c4a6e", marginBottom: 20, lineHeight: 1.6 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0c4a6e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  <span>A <strong>20% restocking fee</strong> will be deducted from your store credit. This covers your prepaid return shipping label.</span>
                </div>

                {error && <p style={{ color: "#dc2626", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{error}</p>}

                <button
                  onClick={handleSubmit}
                  disabled={!form.orderNumber || !form.reason || !form.email || submitting}
                  style={{
                    width: "100%", padding: "16px", borderRadius: 999, border: "none",
                    background: "#111", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer",
                    opacity: (!form.orderNumber || !form.reason || !form.email || submitting) ? 0.5 : 1
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Return Request"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Contact */}
        {tab === "contact" && (
          <div style={card}>
            <SectionLabel text="CONTACT SUPPORT" />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <a href="mailto:support@eleven11marine.com" style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px", borderRadius: 16, border: "1px solid #E0E3EA", background: "#fafafa", textDecoration: "none" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: 1 }}>Email Support</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 3 }}>support@eleven11marine.com</div>
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>Response within 1–2 business days</div>
                </div>
              </a>
              <a href="#" style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px", borderRadius: 16, border: "1px solid #E0E3EA", background: "#fafafa", textDecoration: "none" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: 1 }}>Live Chat</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 3 }}>Available on site</div>
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>Mon–Fri, 9am–6pm EST</div>
                </div>
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}